import React, { useCallback, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { Button, Input } from 'antd';
import { canEdit, deleteBrainFile, listBrainFiles, readBrainFile, writeBrainFile } from '../../services/WorkspacesService';

/**
 * A workspace's brain: notes, conventions and decisions shared by all its projects (vfs-service docs/workspaces.md,
 * "The workspace brain"). The files on the left, the open one on the right; owners and editors edit, add and delete
 * files, viewers read. Agents read a project's own brain first, then this one, then the person's own.
 */
export default function WorkspaceBrain({ workspace }) {
  const [files, setFiles] = useState(null);
  const [open, setOpen] = useState(null); // { path, content }
  const [draft, setDraft] = useState(null); // { path, content } while editing
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const editable = canEdit(workspace);

  const load = useCallback(async() => {
    try {
      setFiles(await listBrainFiles(workspace.id));
    } catch (failed) {
      setError(failed.message);
      setFiles([]);
    }
  }, [workspace.id]);
  useEffect(() => { load(); }, [load]);

  const show = async(path) => {
    setError(null);
    setDraft(null);
    try {
      setOpen(await readBrainFile(workspace.id, path));
    } catch (failed) {
      setError(failed.message);
    }
  };
  const save = async() => {
    setBusy(true);
    setError(null);
    try {
      const path = draft.path.trim();
      await writeBrainFile(workspace.id, path, draft.content);
      setOpen({ path, content: draft.content });
      setDraft(null);
      await load();
    } catch (failed) {
      setError(failed.message);
    } finally {
      setBusy(false);
    }
  };
  const remove = async() => {
    setBusy(true);
    try {
      await deleteBrainFile(workspace.id, open.path);
      setOpen(null);
      await load();
    } catch (failed) {
      setError(failed.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="workspace-section workspace-brain">
      <p className="workspaces-note">
        Shared by every project of this workspace. Agents read a project&apos;s own brain first, then this one, then your own Brain.
      </p>
      {error && <p className="sbx-action-error" role="alert">{error}</p>}
      <div className="workspace-brain-body">
        <div className="workspace-brain-files">
          {editable && <Button onClick={() => setDraft({ path: '', content: '', isNew: true })} size="small">New file</Button>}
          {files === null && <p className="workspaces-note">Loading…</p>}
          {files && files.length === 0 && <p className="workspaces-note">The brain is empty.</p>}
          <ul aria-label="Brain files">
            {(files || []).map((path) => (
              <li key={path}>
                <button className={open?.path === path ? 'active' : ''} onClick={() => show(path)} type="button">{path}</button>
              </li>
            ))}
          </ul>
        </div>
        <div className="workspace-brain-file">
          {draft ? (
            <>
              <Input aria-label="File path" disabled={!draft.isNew} onChange={(event) => setDraft({ ...draft, path: event.target.value })} placeholder="memory/conventions.md" value={draft.path} />
              <Input.TextArea aria-label="File content" autoSize={{ minRows: 10 }} onChange={(event) => setDraft({ ...draft, content: event.target.value })} value={draft.content} />
              <div className="workspace-brain-actions">
                <Button onClick={() => setDraft(null)}>Cancel</Button>
                <Button disabled={!draft.path.trim()} loading={busy} onClick={save} type="primary">Save</Button>
              </div>
            </>
          ) : open ? (
            <>
              <div className="workspace-brain-actions">
                <strong>{open.path}</strong>
                {editable && <Button onClick={() => setDraft({ path: open.path, content: open.content })} size="small">Edit</Button>}
                {editable && <Button danger loading={busy} onClick={remove} size="small">Delete</Button>}
              </div>
              <pre className="workspace-brain-content">{open.content}</pre>
            </>
          ) : (
            <p className="workspaces-note">{files && files.length ? 'Pick a file to read it.' : ''}</p>
          )}
        </div>
      </div>
    </div>
  );
}

WorkspaceBrain.propTypes = {
  workspace: PropTypes.shape({ id: PropTypes.string.isRequired, role: PropTypes.string }).isRequired,
};
